import type { OperationType } from "../operation-types";
import { AGG_DELIMITER, type RiftConfig } from "../types";
import { aggregateToSql } from "./aggregate-to-sql";
import { escapeIdentifier } from "./escape-identifier";

/** A single stat aggregation request: one column with one operation. */
export type StatEntry = {
  column: string;
  operation: OperationType;
};

/**
 * Resolves the column name as it appears in the inner query's output.
 *
 * When groups/aggregates are active the rift aliases multi-op aggregated
 * columns to `column::OP`. Single-op aggregates and CONCAT keep the original
 * name, as do auto-aggregated columns. The stat must reference these output
 * aliases, not the raw source column.
 */
const resolveInnerColumn = (
  column: string,
  riftConfig: RiftConfig | null | undefined,
): string => {
  if (!riftConfig) return column;

  const groups = riftConfig.groups ?? {};
  const aggregates = riftConfig.aggregates ?? {};

  const hasGroups = Object.values(groups).some(Boolean);
  const hasAggregates = Object.values(aggregates).some((ops) => ops?.length);
  if (!hasGroups && !hasAggregates) return column;

  if (groups[column]) return column;

  const ops = aggregates[column];
  if (ops?.length) {
    // Single-op aggregates and CONCAT keep the raw column name.
    if (ops.length === 1 || ops[0] === "CONCAT") return column;
    return `${column}${AGG_DELIMITER}${ops[0]}`;
  }

  return column;
};

/**
 * Wraps a pre-built SQL query with aggregate expressions for the given stat entries.
 *
 * Each entry produces one `AGG(col) AS "column::OPERATION"` in the SELECT.
 * Duplicate `(column, operation)` pairs are deduplicated automatically.
 *
 * When the inner query contains groups/aggregates, column names in the output
 * may differ from the source columns (e.g. `"gmv::SUM"`). Pass the
 * `riftConfig` so stats reference the correct output aliases.
 */
export const statsSql = (
  innerSql: string,
  entries: StatEntry[],
  riftConfig?: RiftConfig | null,
): string | null => {
  const hiddenSet = new Set(riftConfig?.hiddenColumns ?? []);
  const seen = new Set<string>();
  const parts: string[] = [];

  for (const { column, operation } of entries) {
    if (hiddenSet.has(column)) continue;
    const key = `${column}${AGG_DELIMITER}${operation}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const innerCol = resolveInnerColumn(column, riftConfig);
    const expr = aggregateToSql(innerCol, operation);
    parts.push(`${expr} AS ${escapeIdentifier(key)}`);
  }

  if (parts.length === 0) return null;
  return `SELECT ${parts.join(", ")} FROM (${innerSql})`;
};

/**
 * Parses a DuckDB result row with composite `column::OPERATION` keys
 * into a nested `{ [column]: { [operation]: value } }` shape.
 */
export const parseStatsResult = (
  row: Record<string, unknown>,
): Record<string, Record<string, unknown>> => {
  const stats: Record<string, Record<string, unknown>> = {};
  for (const [key, value] of Object.entries(row)) {
    const sepIndex = key.indexOf(AGG_DELIMITER);
    if (sepIndex === -1) continue;
    const column = key.slice(0, sepIndex);
    const operation = key.slice(sepIndex + AGG_DELIMITER.length);
    if (!stats[column]) stats[column] = {};
    stats[column][operation] = value;
  }
  return stats;
};
